/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import MsDyn365 from '@msdyn365-commerce/core';
import { INotification, MfrmNotificationInput } from '../../data-actions/mfrm-notification.action';
import { callUtagLink } from '../../Utilities/analytics/utagCaller';
import { NotificationItem } from './components/notification-item';
import { IMfrmNotificationsData } from './mfrm-notifications.data';
import { IMfrmNotificationsProps } from './mfrm-notifications.props.autogenerated';

export interface IMfrmNotificationsViewProps extends IMfrmNotificationsProps<IMfrmNotificationsData> {
    notification: React.ReactNode;
    pageScrolled: boolean;
}

interface IMfrmNotificationsState {
    isScrolled: boolean;
}

/**
 *
 * MfrmNotifications component
 * @extends {React.PureComponent<IMfrmNotificationsProps<IMfrmNotificationsData>, IMfrmNotificationsState>}
 */
class MfrmNotifications extends React.PureComponent<IMfrmNotificationsProps<IMfrmNotificationsData>, IMfrmNotificationsState> {
    constructor(props: IMfrmNotificationsProps<IMfrmNotificationsData>) {
        super(props);
        this.state = {
            isScrolled: false
        };
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            window.addEventListener('scroll', () => {
                if (window.scrollY >= 52) {
                    this.setState({
                        isScrolled: true
                    });
                } else {
                    this.setState({
                        isScrolled: false
                    });
                }
            });
        }
    }

    public _onDismiss = (itemId: number | undefined): void => {
        const notificationItems: INotification[] | undefined = this.props.data.notification.result?.map((item: INotification) => {
            return {
                id: item.id,
                value: item.value,
                type: item.type,
                status: item.status
            };
        });

        if (itemId && notificationItems) {
            const itemIndex = notificationItems.findIndex(x => x.id === itemId);
            notificationItems.splice(itemIndex, 1);
        }
        this.props.context.actionContext.update(new MfrmNotificationInput(), notificationItems);
    };

    public _onTealiumCall = (): void => {
        if (this.props.data.notification.result && MsDyn365.isBrowser) {
            this.props.data.notification.result?.map((item: INotification, index: number) => {
                let notificationMessage: string | undefined = '';
                if (item.type && item.type === 'promocode') {
                    notificationMessage = item.status === 'SUCCESS' ? this.props.resources.promoSuccessMessage : this.props.resources.promoErrorMessage;
                    notificationMessage = notificationMessage && item.value && notificationMessage?.replace('{0}', item.value);
                }

                const notificationwithoutquotes =  notificationMessage?.replace(/[“”]+/g, '');
                if(notificationMessage!.length > 0) {
                    void callUtagLink(
                        {
                            event: 'add-remove-promos',
                            event_category: 'add/remove promos',
                            event_action: item.status === 'SUCCESS' ? 'Add promo code click' : 'Remove promo code click',
                            event_label: notificationwithoutquotes,
                            event_noninteraction: 'false'
                        }
                    );
                }
            });
        }
    };

    public notificationMessage = (): JSX.Element[] | undefined | null => {
        this._onTealiumCall();
        const expiryTime = this.props.config.notificationExpireTime;
        if (this.props.data.notification.result) {
            return this.props.data.notification.result?.map((item: INotification, index: number) => {
                let notificationMessage: string | undefined = '';
                if (item.type && item.type === 'promocode') {
                    notificationMessage =
                        item.status === 'SUCCESS' ? this.props.resources.promoSuccessMessage : (item.status === 'NOT_APPLIED' ? this.props.resources.promoNotAppliedMessage : this.props.resources.promoErrorMessage);
                    notificationMessage = notificationMessage && item.value && notificationMessage?.replace('{0}', item.value);
                }
                return (
                    <NotificationItem
                        key={item.id}
                        message={notificationMessage ? notificationMessage : item.value}
                        status={item.status}
                        onDismiss={this._onDismiss}
                        id={item.id}
                        expiry={expiryTime}
                    />
                );
            });
        }

        return undefined;
    };

    public render(): JSX.Element | null {
        const renderProps = {
            ...this.props,
            notification: this.notificationMessage(),
            pageScrolled: this.state.isScrolled
        };
        return this.props.renderView(renderProps);
    }
}

export default MfrmNotifications;
